import Phaser from 'phaser';
import { useGameStore } from '../store/gameStore';
import { COLORS, TYPOGRAPHY, createCard } from '../ui/theme';
import type { PocketSize, TimerMode } from '../game/models/Game';

export default class SettingsScene extends Phaser.Scene {
  private pocketSizeValue: PocketSize = 'medium';
  private showTargetLineValue: boolean = false;
  private timerModeValue: TimerMode = 'off';

  constructor() {
    super({ key: 'SettingsScene' });
  }

  create() {
    const { width, height } = this.cameras.main;

    // Load current settings from game store
    const store = useGameStore.getState();
    const game = store.game;
    if (game && game.settings) {
      this.pocketSizeValue = game.settings.pocketSize;
      this.showTargetLineValue = game.settings.showTargetLine;
      this.timerModeValue = game.settings.timerMode;
    }

    // Background
    this.add.rectangle(width / 2, height / 2, width, height, COLORS.background.primary);
    this.add.rectangle(width / 2, height / 4, width, height / 2, COLORS.background.secondary, 0.5);

    // Main card container
    const cardWidth = 600;
    const cardHeight = 650;
    const cardX = (width - cardWidth) / 2;
    const cardY = (height - cardHeight) / 2;
    
    createCard(this, cardX, cardY, cardWidth, cardHeight, 10);

    // Title
    this.add
      .text(width / 2, cardY + 50, 'Settings', {
        fontSize: TYPOGRAPHY.fontSize['4xl'],
        color: COLORS.text.primary,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      })
      .setOrigin(0.5)
      .setDepth(11);

    // Accent line under title
    const lineGraphics = this.add.graphics();
    lineGraphics.fillStyle(COLORS.primary.main, 1);
    lineGraphics.fillRoundedRect(width / 2 - 60, cardY + 80, 120, 3, 2);
    lineGraphics.setDepth(11);

    let currentY = cardY + 130;

    // Number of Players Setting
    this.createNumPlayersSetting(cardX, currentY, cardWidth);
    currentY += 100;

    // Pocket Size Setting
    this.createPocketSizeSetting(cardX, currentY, cardWidth);
    currentY += 100;

    // Target Line Setting
    this.createTargetLineSetting(cardX, currentY, cardWidth);
    currentY += 80;

    // Timer Mode Setting
    this.createTimerModeSetting(cardX, currentY, cardWidth);
    currentY += 100;

    // Back Button
    this.createBackButton(width / 2, cardY + cardHeight - 50);
  }

  private createNumPlayersSetting(cardX: number, y: number, cardWidth: number): void {
    const labelX = cardX + 40;
    const controlX = cardX + cardWidth - 190; // Right align the dropdown

    // Label
    this.add
      .text(labelX, y, 'Number of Players', {
        fontSize: TYPOGRAPHY.fontSize.xl,
        color: COLORS.text.primary,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      })
      .setDepth(11);

    // Description
    this.add
      .text(labelX, y + 25, 'How many players will take turns', {
        fontSize: TYPOGRAPHY.fontSize.sm,
        color: COLORS.text.secondary,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      })
      .setDepth(11);

    // Dropdown
    const store = useGameStore.getState();
    const currentNumPlayers = store.game?.settings.numPlayers || 2;

    this.createDropdown(
      controlX,
      y + 12,
      150,
      ['1 Player', '2 Players', '3 Players', '4 Players', '5 Players', '6 Players'],
      ['1', '2', '3', '4', '5', '6'],
      String(currentNumPlayers),
      (value: string) => {
        const game = useGameStore.getState().game;
        if (game) {
          game.settings.numPlayers = parseInt(value, 10);
          useGameStore.getState().setGame(game);
        }
        this.saveSettings();
        // Restart scene to refresh display
        this.scene.restart();
      }
    );
  }

  private createPocketSizeSetting(cardX: number, y: number, cardWidth: number): void {
    const labelX = cardX + 40;
    const controlX = cardX + cardWidth - 190; // Right align the dropdown

    // Label
    this.add
      .text(labelX, y, 'Pocket Size', {
        fontSize: TYPOGRAPHY.fontSize.xl,
        color: COLORS.text.primary,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      })
      .setDepth(11);

    // Description
    this.add
      .text(labelX, y + 25, 'Larger pockets make it easier to sink letters', {
        fontSize: TYPOGRAPHY.fontSize.sm,
        color: COLORS.text.secondary,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      })
      .setDepth(11);

    // Dropdown
    this.createDropdown(
      controlX,
      y + 12,
      150,
      ['Small', 'Medium', 'Large'],
      ['small', 'medium', 'large'],
      this.pocketSizeValue,
      (value) => {
        this.pocketSizeValue = value as PocketSize;
        this.saveSettings();
        this.scene.restart();
      }
    );
  }

  private createTargetLineSetting(cardX: number, y: number, cardWidth: number): void {
    const labelX = cardX + 40;
    const toggleX = cardX + cardWidth - 100; // Right align the toggle

    // Label
    this.add
      .text(labelX, y, 'Show Target Line', {
        fontSize: TYPOGRAPHY.fontSize.xl,
        color: COLORS.text.primary,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      })
      .setDepth(11);

    // Description
    this.add
      .text(labelX, y + 25, 'Display aim line when shooting for easier targeting', {
        fontSize: TYPOGRAPHY.fontSize.sm,
        color: COLORS.text.secondary,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      })
      .setDepth(11);

    // Toggle switch (right aligned)
    this.createToggle(toggleX, y + 12, this.showTargetLineValue, (newValue) => {
      this.showTargetLineValue = newValue;
      this.saveSettings();
    });
  }

  private createTimerModeSetting(cardX: number, y: number, cardWidth: number): void {
    const labelX = cardX + 40;
    const controlX = cardX + cardWidth - 190; // Right align the dropdown

    // Label
    this.add
      .text(labelX, y, 'Turn Timer', {
        fontSize: TYPOGRAPHY.fontSize.xl,
        color: COLORS.text.primary,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      })
      .setDepth(11);

    // Description
    this.add
      .text(labelX, y + 25, 'Auto-pass if time runs out on board phase', {
        fontSize: TYPOGRAPHY.fontSize.sm,
        color: COLORS.text.secondary,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      })
      .setDepth(11);

    // Dropdown
    this.createDropdown(
      controlX,
      y + 12,
      150,
      ['Off', '5 min', '2 min', '1 min'],
      ['off', '5min', '2min', '1min'],
      this.timerModeValue,
      (value) => {
        this.timerModeValue = value as TimerMode;
        this.saveSettings();
        this.scene.restart();
      }
    );
  }

  private createDropdown(
    x: number,
    y: number,
    width: number,
    labels: string[],
    values: string[],
    currentValue: string,
    onChange: (value: string) => void
  ): void {
    const height = 40;
    const currentIndex = values.indexOf(currentValue);
    const currentLabel = labels[currentIndex] || labels[0];

    // Dropdown background
    const bg = this.add.graphics();
    bg.fillStyle(COLORS.background.primary, 1);
    bg.lineStyle(2, COLORS.border.main, 1);
    bg.strokeRoundedRect(x, y - height / 2, width, height, 8);
    bg.fillRoundedRect(x, y - height / 2, width, height, 8);
    bg.setDepth(11);

    // Selected value text
    this.add
      .text(x + 15, y, currentLabel, {
        fontSize: TYPOGRAPHY.fontSize.base,
        color: COLORS.text.primary,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      })
      .setOrigin(0, 0.5)
      .setDepth(12);

    // Dropdown arrow
    const arrow = this.add
      .text(x + width - 20, y, '▼', {
        fontSize: TYPOGRAPHY.fontSize.sm,
        color: COLORS.text.secondary,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      })
      .setOrigin(0.5)
      .setDepth(12);

    // Make interactive
    const hitArea = new Phaser.Geom.Rectangle(x, y - height / 2, width, height);
    bg.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);

    let dropdownOpen = false;
    let optionGraphics: Phaser.GameObjects.Graphics[] = [];
    let optionTexts: Phaser.GameObjects.Text[] = [];

    bg.on('pointerover', () => {
      if (!dropdownOpen) {
        bg.clear();
        bg.fillStyle(COLORS.background.secondary, 1);
        bg.lineStyle(2, COLORS.primary.light, 1);
        bg.strokeRoundedRect(x, y - height / 2, width, height, 8);
        bg.fillRoundedRect(x, y - height / 2, width, height, 8);
        this.input.setDefaultCursor('pointer');
      }
    });

    bg.on('pointerout', () => {
      if (!dropdownOpen) {
        bg.clear();
        bg.fillStyle(COLORS.background.primary, 1);
        bg.lineStyle(2, COLORS.border.main, 1);
        bg.strokeRoundedRect(x, y - height / 2, width, height, 8);
        bg.fillRoundedRect(x, y - height / 2, width, height, 8);
        this.input.setDefaultCursor('default');
      }
    });

    bg.on('pointerdown', () => {
      if (dropdownOpen) {
        // Close dropdown
        optionGraphics.forEach(g => g.destroy());
        optionTexts.forEach(t => t.destroy());
        optionGraphics = [];
        optionTexts = [];
        dropdownOpen = false;
        arrow.setText('▼');
        this.input.setDefaultCursor('default');
      } else {
        // Open dropdown
        dropdownOpen = true;
        arrow.setText('▲');

        labels.forEach((label, index) => {
          const optionY = y + height / 2 + (index + 1) * height;
          const isSelected = values[index] === currentValue;

          const optionBg = this.add.graphics();
          optionBg.fillStyle(isSelected ? COLORS.primary.main : COLORS.background.secondary, 1);
          optionBg.lineStyle(2, COLORS.border.main, 1);
          optionBg.strokeRoundedRect(x, optionY - height / 2, width, height, 8);
          optionBg.fillRoundedRect(x, optionY - height / 2, width, height, 8);
          optionBg.setDepth(20);

          const optionText = this.add
            .text(x + 15, optionY, label, {
              fontSize: TYPOGRAPHY.fontSize.base,
              color: isSelected ? '#ffffff' : COLORS.text.primary,
              fontFamily: TYPOGRAPHY.fontFamily.default,
            })
            .setOrigin(0, 0.5)
            .setDepth(21);

          const optionHitArea = new Phaser.Geom.Rectangle(x, optionY - height / 2, width, height);
          optionBg.setInteractive(optionHitArea, Phaser.Geom.Rectangle.Contains);

          optionBg.on('pointerover', () => {
            if (!isSelected) {
              optionBg.clear();
              optionBg.fillStyle(COLORS.primary.light, 0.3);
              optionBg.lineStyle(2, COLORS.primary.light, 1);
              optionBg.strokeRoundedRect(x, optionY - height / 2, width, height, 8);
              optionBg.fillRoundedRect(x, optionY - height / 2, width, height, 8);
            }
            this.input.setDefaultCursor('pointer');
          });

          optionBg.on('pointerout', () => {
            if (!isSelected) {
              optionBg.clear();
              optionBg.fillStyle(COLORS.background.secondary, 1);
              optionBg.lineStyle(2, COLORS.border.main, 1);
              optionBg.strokeRoundedRect(x, optionY - height / 2, width, height, 8);
              optionBg.fillRoundedRect(x, optionY - height / 2, width, height, 8);
            }
          });

          optionBg.on('pointerdown', () => {
            onChange(values[index]);
          });

          optionGraphics.push(optionBg);
          optionTexts.push(optionText);
        });
      }
    });
  }

  private createToggle(x: number, y: number, initialValue: boolean, onChange: (newValue: boolean) => void): void {
    const toggleWidth = 60;
    const toggleHeight = 30;
    const knobRadius = 12;

    // Background track
    const track = this.add.graphics();
    if (initialValue) {
      track.fillStyle(COLORS.primary.main, 1);
    } else {
      track.fillStyle(COLORS.border.main, 1);
    }
    track.fillRoundedRect(x - toggleWidth / 2, y - toggleHeight / 2, toggleWidth, toggleHeight, 15);
    track.setDepth(11);
    track.setInteractive(
      new Phaser.Geom.Rectangle(x - toggleWidth / 2, y - toggleHeight / 2, toggleWidth, toggleHeight),
      Phaser.Geom.Rectangle.Contains
    );

    // Knob
    const knobX = initialValue ? x + toggleWidth / 2 - knobRadius - 3 : x - toggleWidth / 2 + knobRadius + 3;
    this.add.circle(knobX, y, knobRadius, 0xffffff).setDepth(12);

    track.on('pointerover', () => {
      this.input.setDefaultCursor('pointer');
    });

    track.on('pointerout', () => {
      this.input.setDefaultCursor('default');
    });

    track.on('pointerdown', () => {
      const newValue = !initialValue;
      onChange(newValue);
      this.scene.restart();
    });
  }

  private createBackButton(x: number, y: number): void {
    const buttonWidth = 200;
    const buttonHeight = 50;

    const bg = this.add.graphics();
    bg.lineStyle(2, COLORS.primary.main, 1);
    bg.strokeRoundedRect(x - buttonWidth / 2, y - buttonHeight / 2, buttonWidth, buttonHeight, 10);
    bg.fillStyle(COLORS.background.primary, 1);
    bg.fillRoundedRect(x - buttonWidth / 2, y - buttonHeight / 2, buttonWidth, buttonHeight, 10);
    bg.setDepth(11);
    bg.setInteractive(
      new Phaser.Geom.Rectangle(x - buttonWidth / 2, y - buttonHeight / 2, buttonWidth, buttonHeight),
      Phaser.Geom.Rectangle.Contains
    );

    this.add
      .text(x, y, 'Back to Menu', {
        fontSize: TYPOGRAPHY.fontSize.lg,
        color: COLORS.primary.text,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      })
      .setOrigin(0.5)
      .setDepth(12);

    bg.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(COLORS.primary.light, 0.1);
      bg.fillRoundedRect(x - buttonWidth / 2, y - buttonHeight / 2, buttonWidth, buttonHeight, 10);
      bg.lineStyle(2, COLORS.primary.light, 1);
      bg.strokeRoundedRect(x - buttonWidth / 2, y - buttonHeight / 2, buttonWidth, buttonHeight, 10);
      this.input.setDefaultCursor('pointer');
    });

    bg.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(COLORS.background.primary, 1);
      bg.fillRoundedRect(x - buttonWidth / 2, y - buttonHeight / 2, buttonWidth, buttonHeight, 10);
      bg.lineStyle(2, COLORS.primary.main, 1);
      bg.strokeRoundedRect(x - buttonWidth / 2, y - buttonHeight / 2, buttonWidth, buttonHeight, 10);
      this.input.setDefaultCursor('default');
    });

    bg.on('pointerdown', () => {
      this.scene.start('MenuScene');
    });
  }

  private saveSettings(): void {
    const store = useGameStore.getState();
    const game = store.game;
    
    if (game) {
      const updatedGame = {
        ...game,
        settings: {
          pocketSize: this.pocketSizeValue,
          showTargetLine: this.showTargetLineValue,
          timerMode: this.timerModeValue,
          numPlayers: game.settings.numPlayers || 2,
        },
      };
      store.setGame(updatedGame);
    } else {
      // If no game exists, create one with settings
      store.startNewGame();
      const newGame = store.game;
      if (newGame) {
        newGame.settings = {
          pocketSize: this.pocketSizeValue,
          showTargetLine: this.showTargetLineValue,
          timerMode: this.timerModeValue,
          numPlayers: newGame.settings.numPlayers || 2,
        };
        store.setGame(newGame);
      }
    }
  }
}
